import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { ScreenHeader } from '../../components/ScreenHeader';
import { getNotificationModuleCapabilities } from '../../config/notificationCapabilities';
import type { ColorPalette } from '../../constants/colors';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useNotificationCount } from '../../context/NotificationCountContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  getNotificationDetails,
  markNotificationAsRead,
} from '../../services/notificationService';
import type { NotificationDetails } from '../../types/notification';
import { formatAttendanceTimestamp } from '../../utils/dateTime';
import { notificationErrorMessage } from '../../utils/notificationErrors';
import { getNotificationDestination } from '../../utils/notificationNavigation';

type Props = NativeStackScreenProps<RootStackParamList, 'NotificationDetails'>;

function Field({
  label,
  value,
  palette,
}: {
  label: string;
  value: string;
  palette: ColorPalette;
}) {
  return (
    <View style={styles.field}>
      <Text style={[styles.label, { color: palette.textMuted }]}>{label}</Text>
      <Text style={[styles.value, { color: palette.textPrimary }]}>{value}</Text>
    </View>
  );
}

export function NotificationDetailsScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getNotificationModuleCapabilities(auth.permissions);
  const notificationCount = useNotificationCount();
  const notificationId = route.params?.notificationId;

  const [details, setDetails] = useState<NotificationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [marking, setMarking] = useState(false);
  const [isRead, setIsRead] = useState(false);
  const [readAt, setReadAt] = useState<string | null>(null);
  const [markError, setMarkError] = useState<string | null>(null);
  const [invalidId, setInvalidId] = useState(false);

  const themed = useMemo(
    () => ({
      body: { ...typography.body, color: palette.textSecondary },
      meta: { ...typography.caption, color: palette.textSecondary },
      warning: { ...typography.caption, color: palette.warning },
    }),
    [palette],
  );

  const markRead = useCallback(async () => {
    if (!capabilities.canMarkRead) return;
    if (!Number.isInteger(notificationId) || notificationId <= 0) return;
    if (isRead || marking) return;

    setMarking(true);
    setMarkError(null);
    try {
      const result = await markNotificationAsRead(notificationId);
      setIsRead(result.isRead);
      setReadAt(result.readAt);
      setDetails(current =>
        current
          ? {
              ...current,
              isRead: true,
              readAt: result.readAt ?? current.readAt,
            }
          : current,
      );
      if (notificationCount.unreadCount != null) {
        notificationCount.setUnreadCountAfterConfirmedRead(
          Math.max(0, notificationCount.unreadCount - 1),
        );
      } else {
        await notificationCount.reloadUnreadCount();
      }
    } catch (caught) {
      setMarkError(
        notificationErrorMessage(
          caught,
          'Unable to mark the notification as read.',
        ),
      );
    } finally {
      setMarking(false);
    }
  }, [
    capabilities.canMarkRead,
    isRead,
    marking,
    notificationCount,
    notificationId,
  ]);

  const load = useCallback(async () => {
    if (!Number.isInteger(notificationId) || notificationId <= 0) {
      setInvalidId(true);
      setDetails(null);
      setLoading(false);
      return;
    }
    if (!capabilities.canViewDetails) {
      setInvalidId(false);
      setDetails(null);
      setError('Notification details are not currently available.');
      setLoading(false);
      return;
    }

    setInvalidId(false);
    setLoading(true);
    setError(null);
    try {
      const result = await getNotificationDetails(notificationId);
      setDetails(result);
      setIsRead(result.isRead);
      setReadAt(result.readAt);
      if (capabilities.canMarkRead && !result.isRead) {
        markNotificationAsRead(notificationId)
          .then(markResult => {
            setIsRead(markResult.isRead);
            setReadAt(markResult.readAt);
            setDetails(current =>
              current
                ? {
                    ...current,
                    isRead: true,
                    readAt: markResult.readAt ?? current.readAt,
                  }
                : current,
            );
            if (notificationCount.unreadCount != null) {
              notificationCount.setUnreadCountAfterConfirmedRead(
                Math.max(0, notificationCount.unreadCount - 1),
              );
            } else {
              notificationCount.reloadUnreadCount().catch(() => undefined);
            }
          })
          .catch(() => undefined);
      }
    } catch (caught) {
      setDetails(null);
      setError(
        notificationErrorMessage(caught, 'Unable to load notification details.'),
      );
    } finally {
      setLoading(false);
    }
  }, [
    capabilities.canMarkRead,
    capabilities.canViewDetails,
    notificationCount,
    notificationId,
  ]);

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  if (loading && !details && !invalidId && !error) {
    return (
      <AppScreen contentStyle={styles.center}>
        <ScreenHeader
          title="Notification"
          onBack={navigation.goBack}
        />
        <AppLoader message="Loading notification" />
      </AppScreen>
    );
  }

  if (invalidId) {
    return (
      <AppScreen contentStyle={styles.center}>
        <ScreenHeader
          title="Notification"
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
          <Text style={themed.body}>Notification was not found.</Text>
        </View>
      </AppScreen>
    );
  }

  if (error && !details) {
    return (
      <AppScreen contentStyle={styles.center}>
        <ScreenHeader
          title="Notification"
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
          <ErrorState message={error} onRetry={load} />
        </View>
      </AppScreen>
    );
  }

  const destination = getNotificationDestination(
    details?.linkedEntity,
    auth.permissions,
    { notificationId: details?.notificationId },
  );
  const title = details?.title?.trim() || 'Notification';
  const typeLabel =
    details?.notificationTypeLabel?.trim() ||
    details?.notificationTypeCode?.trim() ||
    'Not available';
  const message = details?.message?.trim() || 'Not available';
  const createdAt = formatAttendanceTimestamp(details?.createdAt ?? null, null);
  const statusLabel = isRead ? 'Read' : 'Unread';

  return (
    <AppScreen scroll>
      <ScreenHeader
        title={title}
        onBack={navigation.goBack}
      />
      <View style={styles.body}>
        <AppCard style={styles.card}>
          <Field label="Type" value={typeLabel} palette={palette} />
          <Field label="Created" value={createdAt} palette={palette} />
          <Field label="Status" value={statusLabel} palette={palette} />
          {readAt ? (
            <Field
              label="Read at"
              value={formatAttendanceTimestamp(readAt, null)}
              palette={palette}
            />
          ) : null}
          <Text style={[styles.messageLabel, { color: palette.textMuted }]}>
            Message
          </Text>
          <Text style={themed.body}>{message}</Text>
        </AppCard>

        {markError ? (
          <View style={styles.warningBlock} accessibilityRole="alert">
            <Text style={themed.warning}>{markError}</Text>
            <AppButton
              title="Retry Mark as Read"
              variant="secondary"
              onPress={markRead}
              loading={marking}
            />
          </View>
        ) : null}

        <View style={styles.actions}>
          {destination ? (
            <AppButton
              title={
                details?.linkedEntity?.entityType?.toUpperCase() === 'HOLIDAY'
                  ? 'View calendar'
                  : 'View request'
              }
              variant="secondary"
              onPress={() => {
                navigation.navigate(
                  destination.route as never,
                  destination.params as never,
                );
              }}
              accessibilityLabel={
                details?.linkedEntity?.entityType?.toUpperCase() === 'HOLIDAY'
                  ? 'View calendar'
                  : 'View request'
              }
            />
          ) : null}
          {capabilities.canMarkRead && !isRead ? (
            <AppButton
              title="Mark as Read"
              onPress={markRead}
              loading={marking}
              accessibilityLabel="Mark this notification as read"
            />
          ) : null}
        </View>
      </View>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center', gap: spacing.md },
  card: { gap: spacing.sm, marginBottom: spacing.md },
  field: { gap: 2 },
  label: { ...typography.caption },
  value: { ...typography.body },
  messageLabel: { ...typography.caption, marginTop: spacing.sm },
  warningBlock: { gap: spacing.sm, marginBottom: spacing.md },
  actions: { gap: spacing.sm, marginTop: spacing.md },
});
